import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { useIonAlert } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { history } from "../providers/Redux/Reducers/history";
import { useConfig } from "../providers/config";
import { Browser } from "@capacitor/browser";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { usePushTokenStore } from "../providers/push-token";
import useOpenProductDetailSheet from "./useOpenProductDetailSheet";

// Firebase push notification data structure
interface PushNotificationData {
  type: "OPEN_PAGE" | "OPEN_LINK";
  is_product_detail_sheet?: "true" | "false";
  product_id?: string;
  path: string;
}

const useInitFirebaseNotifications = () => {
  const config = useConfig();
  const { t } = useTranslation();
  const [presentAlert] = useIonAlert();
  const { setPushToken } = usePushTokenStore();
  const openProductDetailSheet = useOpenProductDetailSheet();

  useEffect(() => {
    if (config && Capacitor.isNativePlatform() && config.isFCMNotificationsEnabled) {
      const initPushNotifications = async () => {
        try {
          // First check current permission; only prompt if it is not granted
          const current = await FirebaseMessaging.checkPermissions();
          let receive = current.receive;

          if (receive !== "granted") {
            const requested = await FirebaseMessaging.requestPermissions();
            receive = requested.receive;
          }

          console.log("FCM permission status:", receive);

          if (receive === "granted") {
            const tokenRequest = await FirebaseMessaging.getToken();
            console.log("New FCM token generated:", tokenRequest.token);
            setPushToken(tokenRequest.token);

            await FirebaseMessaging.subscribeToTopic({ topic: "all" });

            // Add background handler
            FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
              console.log("Notification action performed:", event);
              if (event.notification.data) {
                const data = event.notification.data as PushNotificationData;
                if (data.type === "OPEN_PAGE") {
                  if (data.is_product_detail_sheet === "true" && data.product_id) {
                    openProductDetailSheet({ id: data.product_id });
                  } else {
                    history.push(data.path, { from: "notification" });
                  }
                }
                if (data.type === "OPEN_LINK") {
                  console.log("Opening link:", data.path);
                  Browser.open({ url: data.path });
                }
              }
            });
          } else {
            console.warn("Push notification permission not granted");
            presentAlert({
              message: t("notificationsPermissionRequiredMessage"),
              header: t("notificationsPermissionRequiredTitle"),
              buttons: [
                {
                  text: t("later"),
                  role: "cancel",
                },
                {
                  text: t("grantPermission"),
                  role: "confirm",
                  handler: async () => {
                    if (Capacitor.getPlatform() === "ios") {
                      await NativeSettings.openIOS({ option: IOSSettings.Notifications });
                    } else if (Capacitor.getPlatform() === "android") {
                      await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
                    }
                  },
                },
              ],
            });
          }
        } catch (e) {
          console.error("Error initializing FCM notifications:", e);
        }
      };

      initPushNotifications();
    }
  }, []);
};

export default useInitFirebaseNotifications;
